import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ContactFormProvider } from "@/contexts/ContactFormContext";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import ScrollToTop from "@/components/ScrollToTop";
import { captureUtmParams } from "@/lib/utm";
import { LAB_COUNT, BRAND_COUNT_LABEL, SKU_COUNT_LABEL } from "@/data/companyFacts";

const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));
const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const WhatsAppButton = lazy(() => import("@/components/WhatsAppButton"));
import CookieConsentBanner from "@/components/CookieConsentBanner";
const NotFound = lazy(() => import("@/pages/NotFound"));

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { name: "google-site-verification", content: "uWPe9M7wPAIb_ReqGna7jlYjTFo7UirTKuoU3KA-s6M" },
      {
        httpEquiv: "Content-Security-Policy",
        content:
          "default-src 'self'; script-src 'self' 'unsafe-inline' https://www.googletagmanager.com https://www.google-analytics.com https://*.googletagmanager.com https://js.hsforms.net https://js.hs-scripts.com https://js.hs-analytics.net https://js-eu1.hs-scripts.com https://js-eu1.hs-analytics.net https://js-eu1.hs-banner.com https://assets.calendly.com https://snap.licdn.com https://*.licdn.com https://www.clarity.ms https://*.clarity.ms https://connect.facebook.net https://pagead2.googlesyndication.com https://googleads.g.doubleclick.net https://www.googleadservices.com; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://assets.calendly.com; font-src 'self' data: https://fonts.gstatic.com; img-src 'self' data: blob: https: ; media-src 'self' https: blob:; connect-src 'self' https://calendly.com https://nrbhtpchcotsgzaaxdyv.supabase.co wss://nrbhtpchcotsgzaaxdyv.supabase.co https://www.google-analytics.com https://*.analytics.google.com https://*.googletagmanager.com https://api.hsforms.com https://forms.hubspot.com https://api-eu1.hubapi.com https://js-eu1.hs-analytics.net https://js-eu1.hs-banner.com https://forms-eu1.hsforms.com https://track-eu1.hubspot.com https://px.ads.linkedin.com https://*.licdn.com https://*.clarity.ms https://pagead2.googlesyndication.com https://googleads.g.doubleclick.net https://www.google.com https://stats.g.doubleclick.net https://www.facebook.com; frame-src 'self' https://www.youtube.com https://www.youtube-nocookie.com https://*.hsforms.net https://calendly.com https://td.doubleclick.net; worker-src 'self' blob:; object-src 'none'; base-uri 'self'; form-action 'self' https://api.hsforms.com",
      },
      { name: "author", content: "GoHub" },
      { name: "ai-content-declaration", content: "public,citation-ok" },
    ],
    links: [
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/favicon.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "shortcut icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "manifest", href: "/manifest.json" },
      { rel: "alternate", type: "text/markdown", href: "/llms.txt", title: "LLM-friendly site overview" },
      { rel: "alternate", type: "text/plain", title: "LLM-friendly summary", href: "/llms.txt" },
      { rel: "alternate", type: "text/plain", title: "LLM-friendly full content", href: "/llms-full.txt" },
      { rel: "preconnect", href: "https://nrbhtpchcotsgzaaxdyv.supabase.co", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://assets.calendly.com", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://calendly.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://assets.calendly.com" },
      { rel: "dns-prefetch", href: "https://calendly.com" },
      { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/urbanist-latin.woff2", crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/mulish-latin.woff2", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "/fonts/fonts.css" },
      { rel: "stylesheet", href: appCss },
    ],
    scripts: [
      {
        children: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          (function(){
            var stored = null;
            try { stored = JSON.parse(localStorage.getItem('gohub_cookie_consent') || 'null'); } catch (e) { stored = null; }
            if (stored) {
              gtag('consent', 'default', {
                'ad_storage': stored.marketing ? 'granted' : 'denied',
                'ad_user_data': stored.marketing ? 'granted' : 'denied',
                'ad_personalization': stored.marketing ? 'granted' : 'denied',
                'analytics_storage': stored.analytics ? 'granted' : 'denied',
                'functionality_storage': (stored.functionality || stored.necessary) ? 'granted' : 'denied',
                'personalization_storage': stored.functionality ? 'granted' : 'denied',
                'security_storage': 'granted'
              });
            } else {
              gtag('consent', 'default', {
                'ad_storage': 'denied',
                'ad_user_data': 'denied',
                'ad_personalization': 'denied',
                'analytics_storage': 'denied',
                'functionality_storage': 'denied',
                'personalization_storage': 'denied',
                'security_storage': 'granted',
                'wait_for_update': 500
              });
            }
          })();
          gtag('js', new Date());
          gtag('config', 'G-0YQKV2B6FZ');
        `,
      },

      {
        children: `
          (function(){
            function loadAnalytics(){
              if(window.__analyticsLoaded)return;window.__analyticsLoaded=true;
              var g=document.createElement('script');g.async=true;
              g.src='https://www.googletagmanager.com/gtag/js?id=G-0YQKV2B6FZ';
              document.head.appendChild(g);
              var w=window,d=document,l='dataLayer',i='GTM-PPV4WN5';
              w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
              var j=d.createElement('script');j.async=true;
              j.src='https://www.googletagmanager.com/gtm.js?id='+i;
              d.head.appendChild(j);
            }
            if('requestIdleCallback' in window){requestIdleCallback(loadAnalytics,{timeout:3500});}
            else{setTimeout(loadAnalytics,3500);}
          })();
        `,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://www.goptichub.com/#organization",
          name: "GoHub",
          legalName: "Global Optic Hub Ltd",
          alternateName: ["goptichub", "Global Optic Hub", "GoHub Eyewear"],
          url: "https://www.goptichub.com",
          logo: {
            "@type": "ImageObject",
            url: "https://www.goptichub.com/favicon.png",
            width: 512,
            height: 512,
            caption: "GoHub",
          },
          image: "https://www.goptichub.com/favicon.png",
          description: `GoHub is a B2B platform that lets online stores, pharmacies and eyewear brands sell prescription eyewear without holding inventory. ${SKU_COUNT_LABEL} frame SKUs, ${BRAND_COUNT_LABEL} brands, AI optician, virtual try-on and a ${LAB_COUNT}-lab EU and US fulfillment network.`,
          email: "marketing@goptichub.com",
          telephone: "+972-3-9229070",
          founder: [
            { "@type": "Person", name: "Dror David", jobTitle: "Chief Executive Officer" },
          ],
          employee: [
            { "@type": "Person", name: "Dror David", jobTitle: "Chief Executive Officer" },
            { "@type": "Person", name: "Niels Kramer", jobTitle: "Chief Marketing Officer" },
            { "@type": "Person", name: "David Megera", jobTitle: "Chief Technology Officer" },
          ],
          areaServed: [
            { "@type": "Place", name: "European Union" },
            { "@type": "Country", name: "United States" },
          ],
          knowsAbout: [
            "prescription eyewear",
            "optical lab fulfillment",
            "virtual try-on",
            "pupillary distance measurement",
            "prescription lens configuration",
            "B2B e-commerce",
            "eyewear dropshipping",
            "ophthalmic lenses",
          ],
          contactPoint: [
            {
              "@type": "ContactPoint",
              contactType: "sales",
              email: "marketing@goptichub.com",
              telephone: "+972-3-9229070",
              availableLanguage: ["en"],
              areaServed: ["EU", "US"],
            },
          ],
          sameAs: [
            "https://www.linkedin.com/company/goptichub",
            "https://www.facebook.com/goptichub",
            "https://www.youtube.com/@goptichub",
            "https://www.wikidata.org/wiki/Q139557088",
            "https://finder.startupnationcentral.org/company_page/global-optic-hub",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://www.goptichub.com/#website",
          name: "GoHub",
          url: "https://www.goptichub.com",
          publisher: { "@type": "Organization", name: "GoHub" },
          inLanguage: "en",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-PPV4WN5"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    if (typeof window === "undefined") return;
    captureUtmParams();
  }, []);

  return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Suspense fallback={null}>
            <Toaster />
            <Sonner />
            <WhatsAppButton />
          </Suspense>
          <ScrollToTop />
          <Suspense fallback={null}>
            <CookieConsentBanner />
          </Suspense>
          <ContactFormProvider>
            <Outlet />
          </ContactFormProvider>
        </TooltipProvider>
      </QueryClientProvider>
  );
}

function NotFoundComponent() {
  return (
    <Suspense fallback={null}>
      <NotFound />
    </Suspense>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();

  console.error(error);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
      <div className="max-w-md text-center">
        <h1 className="mb-4 text-2xl font-bold">This page didn't load</h1>
        <p className="mb-6 text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="flex justify-center gap-3">
          <button
            className="rounded-md bg-primary px-4 py-2 text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a
            href="/"
            className="rounded-md border border-border bg-card px-4 py-2 text-card-foreground"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
